import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth"; // Added import
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid2 as Grid,
  Chip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UpgradeIcon from "@mui/icons-material/Upgrade";

// Styled components
const ProfilePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "20px",
  background: "#ffffff",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.05)",
  border: "1px solid rgba(0, 0, 0, 0.05)",
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #2ecc71 0%, #66bb6a 100%)",
  padding: theme.spacing(3),
  borderRadius: "20px 20px 0 0",
  color: "#fff",
  textAlign: "center",
  margin: "-32px -32px 32px -32px",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(1.5, 4),
  textTransform: "none",
  fontWeight: 700,
  fontSize: "1rem",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    "&:hover fieldset": {
      borderColor: "#2ecc71",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2ecc71",
    },
  },
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.5),
    fontSize: "1rem",
  },
  "& .MuiInputLabel-root": {
    color: "#34495e",
    fontWeight: 500,
  },
}));

const Profile = () => {
  const [userData, setUserData] = useState({
    fullName: "",
    email: "",
    gradeLevel: "",
    learningGoals: "",
    accountType: "trial",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData({
            fullName: userSnap.data().fullName || "",
            email: user.email || "",
            gradeLevel: userSnap.data().gradeLevel || "",
            learningGoals: userSnap.data().learningGoals || "",
            accountType: userSnap.data().status || "trial",
          });
        }
        setIsLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchProfile(user);
      } else {
        navigate("/sign-in");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setIsLoading(true);
    try {
      const userRef = doc(db, "users", auth.currentUser.uid);
      await updateDoc(userRef, {
        fullName: userData.fullName,
        gradeLevel: userData.gradeLevel,
        learningGoals: userData.learningGoals,
      });
      setIsEditing(false);
      setSnackbar({ open: true, message: "Profile updated successfully!", severity: "success" });
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbar({ open: true, message: "Error updating profile", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate("/dashboard/subscriptions");
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 10 }}>
      <ProfilePaper elevation={8}>
        <HeaderBox>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: "#fff",
              color: "#2ecc71",
              fontSize: 40,
              fontWeight: 700,
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
              mx: "auto",
              mb: 2,
            }}
          >
            {userData.fullName?.charAt(0).toUpperCase() || "U"}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {userData.fullName || "User Profile"}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
            Account: <b>{userData.accountType}</b>
          </Typography>
        </HeaderBox>
        <Box sx={{ p: 2 }}>
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <StyledTextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={userData.fullName}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              InputProps={{ readOnly: !isEditing }}
            />
            <StyledTextField
              fullWidth
              label="Email"
              name="email"
              value={userData.email}
              disabled
              variant="outlined"
              InputProps={{ readOnly: true }}
            />
            <StyledTextField
              fullWidth
              label="Grade Level"
              name="gradeLevel"
              value={userData.gradeLevel}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              InputProps={{ readOnly: !isEditing }}
            />
            <StyledTextField
              fullWidth
              label="Learning Goals"
              name="learningGoals"
              value={userData.learningGoals}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              multiline
              rows={4}
              InputProps={{ readOnly: !isEditing }}
            />
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4, flexWrap: "wrap" }}>
              {isEditing ? (
                <>
                  <StyledButton
                    variant="contained"
                    color="success"
                    onClick={handleSave}
                    disabled={isLoading}
                    startIcon={<SaveIcon />}
                    sx={{ background: "linear-gradient(90deg, #2ecc71, #66bb6a)" }}
                  >
                    Save Changes
                  </StyledButton>
                  <StyledButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleEditToggle}
                    disabled={isLoading}
                    startIcon={<CancelIcon />}
                    sx={{ borderColor: "#34495e", color: "#34495e" }}
                  >
                    Cancel
                  </StyledButton>
                </>
              ) : (
                <>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={handleEditToggle}
                    startIcon={<EditIcon />}
                    sx={{ background: "linear-gradient(90deg, #3498db, #42a5f5)" }}
                  >
                    Edit Profile
                  </StyledButton>
                  {userData.accountType !== "active" && (
                    <StyledButton
                      variant="contained"
                      color="warning"
                      onClick={handleUpgrade}
                      startIcon={<UpgradeIcon />}
                      sx={{ background: "linear-gradient(90deg, #f39c12, #f1c40f)" }}
                    >
                      Upgrade to Premium
                    </StyledButton>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </ProfilePaper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;